import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import { track } from '@lib/posthog';
import type {
  Musteri,
  CreateMusteriInput,
  ListMusteriQuery,
  KartvizitSonuc,
} from '@/types/musteri';

const QUERY_KEY = 'musteriler';

export function useMusteriler(query: ListMusteriQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('musteriler', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<ListResponse<Musteri>>(),
  });
}

export function useMusteri(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api.get(`musteriler/${id}`).json<{ data: Musteri }>().then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateMusteri() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMusteriInput) =>
      api.post('musteriler', { json: input }).json<{ data: Musteri }>().then((r) => r.data),
    onSuccess: (data) => {
      track('musteri_olusturuldu', { musteri_id: data.id });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Kartvizit fotoğrafını OCR'dan geçirip ayrıştırılmış alanları döner.
 * OCR (Tesseract WASM) ilk çağrıda dil verisi indirdiği için uzun sürebilir →
 * timeout genişletilmiştir. Müşteri KAYDETMEZ; sadece forma hazır alan döner.
 */
export function useKartvizitTani() {
  return useMutation({
    mutationFn: (dosya: File) => {
      const formData = new FormData();
      formData.append('dosya', dosya);
      return api
        .post('musteriler/kartvizit-tani', { body: formData, timeout: 90_000 })
        .json<{ data: KartvizitSonuc }>()
        .then((r) => r.data);
    },
    onSuccess: () => {
      track('kartvizit_tarandi');
    },
  });
}

/**
 * İstemcide (tarayıcı/mobil cihaz-içi STT) metne çevrilmiş konuşmadan müşteri
 * alanlarını tanır. Müşteri KAYDETMEZ; sadece forma hazır alan döner.
 */
export function useSesTani() {
  return useMutation({
    mutationFn: (metin: string) =>
      api
        .post('musteriler/ses-tani', { json: { metin } })
        .json<{ data: KartvizitSonuc }>()
        .then((r) => r.data),
    onSuccess: () => {
      track('ses_ile_musteri_tarandi');
    },
  });
}

export function useUpdateMusteri(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateMusteriInput>) =>
      api.patch(`musteriler/${id}`, { json: input }).json<{ data: Musteri }>().then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

/**
 * Müşteri Excel/CSV import — preview ve apply mutation'larını döner.
 * Dialog'da tek tek kullanılır; başarı sonrası `musteriler` query'sini invalidate eder.
 */
export function useImportMusteri() {
  const qc = useQueryClient();
  const preview = useMutation({
    mutationFn: (payload: { rows: Record<string, unknown>[] }) =>
      api.post('musteriler/import-preview', { json: payload }).json<{ data: unknown }>(),
  });
  const apply = useMutation({
    mutationFn: (payload: {
      olusturulacak: Record<string, unknown>[];
      guncellenecek: Record<string, unknown>[];
    }) =>
      api
        .post('musteriler/import-apply', { json: payload })
        .json<{ data: { olusturulan: number; guncellenen: number } }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return { preview, apply };
}

export function useDeleteMusteri() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`musteriler/${id}`),
    onSuccess: (_data, id) => {
      track('musteri_silindi', { musteri_id: id });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
